import React, { useState, useEffect, useCallback } from 'react';

import GalleryDemoLayout from './GalleryDemoLayout';
import { useGalleryLightbox } from './GalleryLightbox';
import { PHOTOS } from './galleryDemoData';

/* Demo 8 — 3D depth stack. Cards are stacked in Z-depth like a deck; step
   through to bring the next card forward. Click the front card to zoom. */
const N = PHOTOS.length;

const GalleryStack = () => {
    const lb = useGalleryLightbox(PHOTOS);
    const [active, setActive] = useState(0);

    const go = useCallback((dir) => setActive((a) => (a + dir + N) % N), []);

    useEffect(() => {
        if (lb.index >= 0) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'ArrowRight') go(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go, lb.index]);

    const cardStyle = (i) => {
        const pos = (i - active + N) % N; // 0 = front, grows toward the back
        if (pos > 3) {
            return { opacity: 0, pointerEvents: 'none', transform: 'translate(-50%, -50%) translateZ(-460px) scale(.8)' };
        }
        return {
            transform: `translate(-50%, -50%) translateY(${-pos * 22}px) translateZ(${-pos * 72}px) scale(${1 - pos * 0.06})`,
            zIndex: 100 - pos,
            opacity: pos === 0 ? 1 : 0.92 - pos * 0.16,
        };
    };

    return (
        <GalleryDemoLayout
            demoLabel="Demo 8"
            kicker="3D Depth Stack"
            title="Deal The Deck"
            lead="The photos are stacked in 3D depth like a deck of cards. Use the arrows (or arrow keys) to deal the next one forward, and click the front card to open it full-screen."
        >
            <div className="wrap">
                <div className="gstack reveal">
                    <div className="gstack__stage">
                        {PHOTOS.map((p, i) => (
                            <button
                                type="button"
                                key={i}
                                className={"gstack__card" + (i === active ? " is-front" : "")}
                                style={cardStyle(i)}
                                onClick={() => (i === active ? lb.open(i) : setActive(i))}
                                aria-label={i === active ? `Open photo: ${p.cap}` : `Bring forward: ${p.cap}`}
                                tabIndex={((i - active + N) % N) > 3 ? -1 : 0}
                            >
                                <img src={p.src} alt={p.cap} draggable="false" />
                                {i === active && <span className="gstack__cap">{p.cap}</span>}
                            </button>
                        ))}
                    </div>

                    <div className="gcf__controls">
                        <button type="button" className="gcf__btn" onClick={() => go(-1)} aria-label="Previous">
                            <svg viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                        <span className="gstack__count">{active + 1} / {N}</span>
                        <button type="button" className="gcf__btn" onClick={() => go(1)} aria-label="Next">
                            <svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                    </div>
                </div>
            </div>
            {lb.lightbox}
        </GalleryDemoLayout>
    );
};

export default GalleryStack;
